import { useEffect, useState } from 'react';
import type { AuditAction, AuditActionInput, AuditActionPriority } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { PRIORITY_LABEL, isoDateFromToday } from './auditStatus';
import { useCompanyUsers } from './useCompanyUsers';

interface AuditActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The auditing customer (owner list). */
  customerId: number;
  auditId: number;
  /** Edit this action; omitted = raise a new one. */
  action?: AuditAction | null;
  /** Raising from a finding: prefilled description and the answer it came from. */
  prefill?: { description: string; response: number | null };
  /** true on success, else the message shown in the dialog. */
  onSave: (payload: AuditActionInput) => Promise<true | string>;
}

const PRIORITIES = Object.keys(PRIORITY_LABEL) as AuditActionPriority[];

/**
 * Raise or edit a corrective action. Defaults for a new one: owner = me,
 * due in 30 days, Medium priority. Closing is a separate step (notes required).
 */
export default function AuditActionDialog({
  open, onOpenChange, customerId, auditId, action, prefill, onSave,
}: AuditActionDialogProps) {
  const { user } = useAuth();
  const { options, loading, error: usersError, retry } = useCompanyUsers(open ? customerId : null);
  const [description, setDescription] = useState<string>('');
  const [owner, setOwner] = useState<string>('');
  const [supplierContact, setSupplierContact] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [priority, setPriority] = useState<AuditActionPriority>('medium');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDescription(action?.description ?? prefill?.description ?? '');
    setOwner(String(action?.owner ?? user?.id ?? ''));
    setSupplierContact(action?.supplier_contact ?? '');
    setDueDate(action?.due_date ?? isoDateFromToday(30));
    setPriority(action?.priority ?? 'medium');
  }, [open, action, prefill, user]);

  const valid = description.trim() !== '' && owner !== '' && dueDate !== '';

  const submit = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);
    const result = await onSave({
      audit: auditId,
      response: action ? action.response : (prefill?.response ?? null),
      description: description.trim(),
      owner: Number(owner),
      supplier_contact: supplierContact.trim(),
      due_date: dueDate,
      priority,
    });
    setSaving(false);
    if (result === true) onOpenChange(false);
    else setError(result);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{action ? 'Edit action' : 'Raise action'}</DialogTitle>
          <DialogDescription>
            Corrective actions are tracked by your team. The supplier does not see them.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="audit-action-description">Action *</Label>
            <Textarea id="audit-action-description" value={description} rows={3} className="text-base"
              onChange={(e) => setDescription(e.target.value)} placeholder="What needs to happen?" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="audit-action-owner">Owner *</Label>
            <SearchableSelect id="audit-action-owner" options={options} value={owner} onChange={setOwner} size="lg"
              placeholder={loading ? 'Loading users…' : 'Choose the owner'} disabled={loading} />
            {usersError && (
              <p className="flex items-center gap-2 text-sm text-destructive">
                Could not load your team&apos;s users.
                <Button type="button" variant="link" className="h-auto p-0" onClick={retry}>Retry</Button>
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="audit-action-contact">Supplier contact</Label>
            <Input id="audit-action-contact" className="h-12 text-base" value={supplierContact} onChange={(e) => setSupplierContact(e.target.value)}
              placeholder="Name, email or phone at the supplier" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="audit-action-due">Due date *</Label>
              <Input id="audit-action-due" type="date" className="h-12 text-base" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as AuditActionPriority)}>
                <SelectTrigger className="h-12 text-base" aria-label="Priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p} className="py-3 text-base">{PRIORITY_LABEL[p]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button size="lg" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button size="lg" onClick={submit} disabled={!valid || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save action
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
